/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */

import {Ref, ref, watch} from 'vue';
import {APIService} from '@/core/util/services/api.service';

export interface BuzzLinkPreviewData {
  url: string;
  title: string | null;
  description: string | null;
  image: string | null;
}

const previewCache = new Map<string, BuzzLinkPreviewData>();
const PREVIEW_DEBOUNCE_MS = 400;

const getAppBaseUrl = (): string => {
  return (window as typeof window & {appGlobal: {baseUrl: string}}).appGlobal
    .baseUrl;
};

export default function useBuzzLinkPreview(url: Ref<string | null>) {
  const preview = ref<BuzzLinkPreviewData | null>(null);
  const loading = ref(false);
  let debounceTimer: ReturnType<typeof setTimeout> | undefined;
  let requestId = 0;

  const http = new APIService(getAppBaseUrl(), '');
  http.setIgnorePath('/api/v2/buzz/link-preview');

  const fetchPreview = async (linkUrl: string | null) => {
    if (!linkUrl) {
      preview.value = null;
      return;
    }

    const cached = previewCache.get(linkUrl);
    if (cached) {
      preview.value = cached.image ? cached : null;
      return;
    }

    const currentRequestId = ++requestId;
    loading.value = true;
    try {
      const response = await http.request({
        method: 'GET',
        url: '/api/v2/buzz/link-preview',
        params: {url: linkUrl},
      });
      if (currentRequestId !== requestId) {
        return;
      }

      const result = response.data?.data as BuzzLinkPreviewData | undefined;
      if (result?.image) {
        previewCache.set(linkUrl, result);
        preview.value = result;
      } else {
        preview.value = null;
      }
    } catch {
      if (currentRequestId === requestId) {
        preview.value = null;
      }
    } finally {
      if (currentRequestId === requestId) {
        loading.value = false;
      }
    }
  };

  watch(
    url,
    (nextUrl) => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      if (!nextUrl) {
        preview.value = null;
        return;
      }
      debounceTimer = setTimeout(() => {
        fetchPreview(nextUrl);
      }, PREVIEW_DEBOUNCE_MS);
    },
    {immediate: true},
  );

  return {
    preview,
    loading,
  };
}
